import { useEffect, useState, useCallback, useMemo } from 'react';

import {
  SharedCardData,
  ProcessedSharedCard,
  SharedCardsPageState,
} from '../types';

interface UseSharedCardsReturn {
  state: SharedCardsPageState;
  selectCard: (card: ProcessedSharedCard) => void;
  setActiveTab: (tab: 'my-card' | 'qr-code') => void;
  refreshQRCode: () => void;
  clearError: () => void;
}

const processSharedCardsData = (
  jsonData: SharedCardData[],
): ProcessedSharedCard[] => {
  return jsonData
    .map((item, index) => {
      const hasValidCardInfo =
        !Array.isArray(item.cardInfo) &&
        item.cardInfo.result === 1 &&
        item.cardInfo.data &&
        item.cardInfo.data.length > 0;

      const cardData = hasValidCardInfo ? (item.cardInfo as any).data[0] : null;

      const qrCodeData = cardData
        ? {
            token: item.token,
            ds_trilha_3: cardData.ds_trilha_3,
            descTrailQrCode: cardData.descTrailQrCode,
          }
        : undefined;

      return {
        id: `shared-card-${item.userUnimedId}-${index}`,
        userUnimedId: item.userUnimedId,
        userPlanCode: item.userPlanCode,
        insuredName: item.insuredName,
        token: item.token,
        cardData,
        isValid: hasValidCardInfo,
        qrCodeData,
      };
    })
    .filter((card) => card.isValid);
};

export const useSharedCards = (
  jsonData: SharedCardData[],
): UseSharedCardsReturn => {
  const availableCards = useMemo(
    () => processSharedCardsData(jsonData),
    [jsonData],
  );

  const [state, setState] = useState<SharedCardsPageState>({
    selectedCard: availableCards.length > 0 ? availableCards[0] : null,
    availableCards,
    activeTab: 'my-card',
    loading: false,
    error: null,
  });

  useEffect(() => {
    setState({
      selectedCard: availableCards.length > 0 ? availableCards[0] : null,
      availableCards,
      activeTab: 'my-card',
      loading: false,
      error: null,
    });
  }, [availableCards]);

  const selectCard = useCallback((card: ProcessedSharedCard) => {
    setState((prev) => ({
      ...prev,
      selectedCard: card,
      activeTab: 'my-card',
      error: null,
    }));
  }, []);

  const setActiveTab = useCallback((tab: 'my-card' | 'qr-code') => {
    setState((prev) => ({
      ...prev,
      activeTab: tab,
    }));
  }, []);

  const refreshQRCode = useCallback(() => {
    setState((prev) => ({
      ...prev,
      loading: true,
    }));

    setTimeout(() => {
      setState((prev) => ({
        ...prev,
        loading: false,
      }));
    }, 1000);
  }, []);

  const clearError = useCallback(() => {
    setState((prev) => ({
      ...prev,
      error: null,
    }));
  }, []);

  return {
    state,
    selectCard,
    setActiveTab,
    refreshQRCode,
    clearError,
  };
};
